import { Component } from "react";
import { connect, ConnectedComponent } from "react-redux";
import { error } from "../../../Common/Notifications/helper";
import { FormState, ICompositionParams, IStore, LockStatus } from "../DataTypes/storeTypes";
import { IFormData } from "../DataTypes/formTypes";
import {
    goToFormsPage,
    onFormSave,
    removeClientFile,
    setFormStateIfChanged,
    setSubmitRequested,
} from "../Actions/actions";
import { showStripeDialog } from "../Actions/stripeActions";
import { Timer } from "../../../Common/timer";
import { ConfirmationDialog, IConfirmationProps } from "../../../Modals/Confirmation";
import { cloneComposition } from "../Helpers/compositionHelper";
import { DisplayHelper } from "../Helpers/displayHelper";
import { CompositionExposed as CompositionExposedNew } from "common.ui/CustomForms/Editor";
import { SpinnerButton } from "../../../Common/SpinnerButton";
import { StripeModal } from "./Stripe/StripeModal";
import { ICustomFormEditorParams } from "common.ui/CustomForms/Editor/DataTypes/IStore";

interface IProps {
    formState?: FormState;
    status: LockStatus;
    isBusy: boolean;
    submitRequested: boolean;

    compositionParams: ICompositionParams;

    sgName: string;
    formName: string;

    setFormState(state: FormState): void;
    saveForm(data: IFormData): void;

    openSubmitConfirmation(): void;
    closeSubmitConfirmation(): void;
    removeClientFile(fileId: number);
    showStripeModal(updateFieldCallback: (value) => void): void;
    redirectToFormsPage(): void;
}

export class CustomForm extends Component<IProps> {
    compositionInstance: CompositionExposedNew;

    saveByTimer() {
        if (this.props.formState === FormState.Dirty) {
            this.saveCustomForm(LockStatus.AutoDraft);
        }
    }

    timer: Timer = new Timer(this.saveByTimer.bind(this), 5000);

    componentDidMount() {
        this.setTimer();
    }

    componentWillUnmount() {
        this.timer.stop();
    }

    componentDidUpdate() {
        this.setTimer();

        if (this.props.status === LockStatus.Locked) {
            this.props.redirectToFormsPage();
        }
    }

    setTimer() {
        if (this.props.status === LockStatus.Locked) {
            this.timer.stop();
        } else {
            this.timer.reset();
        }
    }

    getFormData(status: LockStatus): IFormData {
        const formId = this.props.compositionParams.templateId;
        const composition = this.compositionInstance.getEntryComposition();

        return {
            id: formId,
            composition: cloneComposition(composition),
            status: status,
        };
    }

    saveCustomForm(status: LockStatus) {
        const data = this.getFormData(status);
        this.props.saveForm(data);
    }

    handleFinishBtn = async () => {
        const isValid = await this.compositionInstance.runValidation();
        if (isValid) {
            this.props.openSubmitConfirmation();
        } else {
            error("Your form could not be submitted. Please fix any errors and try again.");
        }
    };

    onCompositionChange(composition: any, isCompositionValid: boolean) {
        if (this.props.status !== LockStatus.Locked) {
            this.props.setFormState(FormState.Dirty);
            this.setTimer();
        }
    }

    removeClientFile(fileId: number) {
        return this.props.removeClientFile(fileId);
    }

    renderSubmitConfirmation() {
        const confirmSettings: IConfirmationProps = {
            text: `You are about to submit this form to ${this.props.sgName}. You will not be able to edit it after it has been submitted.`,
            yesButtonTitle: "Yes",
            yesButtonCallback: () => {
                this.saveCustomForm(LockStatus.Locked);
                this.props.closeSubmitConfirmation();
            },
            noButtonTitle: "Cancel",
            noButtonCallback: this.props.closeSubmitConfirmation,
            onModalClosed: this.props.closeSubmitConfirmation,
        };

        return <ConfirmationDialog {...confirmSettings} />;
    }

    renderComposition() {
        const compositionParams: ICustomFormEditorParams = {
            ...this.props.compositionParams,

            isPortalCustomFormView: true,
            displayModes: false,
            onContentChange: this.onCompositionChange.bind(this),
            removeClientFile: this.removeClientFile.bind(this),
            getUploadError: this.getUploadError.bind(this),
            onRemoveClientFile: this.saveByTimer.bind(this),
            onClientFileAdded: this.saveByTimer.bind(this),
            showErrorToUser: this.showErrorToUser.bind(this),
            showStripeModal: this.showStripeModal.bind(this),
            isEditable: false,
            templateName: "",
            promptUnsavedChanges: false,
            shouldUpdateComposition: false,
            afterSaveCallback: () => {},
            editCustomFormAct: () => {},
            expiryPeriod: 0,
            isShownOnClientPortal: false,
            showFormsOnClientPortal: false,
            previewClientFile: () => {},
            clientFileDownloadUrl: "",
            isCreditCardFieldEnabled: false,
            hideDeprecatedGenderOptions: false,
            renderFroalaInIFrame: false,
            isMultiContactEnabled: false,
            getDrawingsUrl: "",
            uploadDrawing: "",
            drawingSizeLimit: 0,
            updateDrawingUrl: "",
            backToTemplatesUrl: "",
            mergeFieldInfoUrl: "",
            saveTemplateUrl: "",
            isImportPreview: false,
            localization: { state: "" },
            notification: { hasSupplier: false, emails: "" },
            labels: {},
            isCustomFormTemplateEditView: false,
            isClientCustomFormEditView: false,
            updateDrawingDataUrl: () => {},
            defaultFieldTitle: "",
            deleteTemplateSettings: {
                enableDelete: false,
                deleteTemplateUrl: "",
                listTemplateUrl: "",
                validationUrl: "",
                restoreTemplateUrl: "",
                isActive: false,
            },
            scoring: {
                isCustomFormsScoringEnabled: false,
                isScoreEnabled: false,
                displayScoresInPortal: false,
                assessmentScores: [],
            },
        };

        const formStateName = DisplayHelper.getFormStateDisplayName(this.props.formState);

        return (
            <>
                <div className="row mt-5">
                    <div className="col-12">
                        <div className="fsize16 semibold">
                            Please complete the form below and click the Finish button when it is complete. Your data is
                            automatically saved as you go.
                        </div>

                        <h4 className="question mt-4 mb-0">{this.props.formName}</h4>
                    </div>
                </div>

                <CompositionExposedNew
                    ref={(instance) => (this.compositionInstance = instance)}
                    params={compositionParams}
                />

                <div className="row mb-5 mt-1 d-print-none">
                    <div className="col-12 mb-3 text-right">
                        <label className="bold position-relative mr-3 mt-2 ml-3">{formStateName}</label>

                        <SpinnerButton
                            text="Finish"
                            loadingText="Processing"
                            isBusy={this.props.isBusy}
                            onClick={this.handleFinishBtn}
                            type="button"
                        />

                        {this.props.submitRequested && this.renderSubmitConfirmation()}
                    </div>
                </div>
            </>
        );
    }

    render() {
        return (
            <>
                {this.props.status === LockStatus.Locked ? (
                    <div className="text-center mt-5">
                        <p>Thank you. We have received your form.</p>
                    </div>
                ) : (
                    this.renderComposition()
                )}

                <StripeModal />
            </>
        );
    }

    private getUploadError(responseText: string): Error {
        let message = "Upload failed.";
        try {
            const parsed = JSON.parse(responseText) as { message?: string; title?: string };
            message = parsed?.message ?? parsed?.title ?? message;
        } catch {
            // non-JSON body
        }
        error(message);
        return new Error();
    }

    showErrorToUser(message: string) {
        error(message);
    }

    showStripeModal(updateFieldCallback: (value) => void) {
        this.props.showStripeModal(updateFieldCallback);
    }
}

const mapStateToProps = (state: IStore, ownProps: IProps): IProps => {
    return {
        ...ownProps,
        sgName: state.initialSettings.sgName,
        formName: state.initialSettings.formName,
        formState: state.common.formState,
        status: state.common.status,
        isBusy: state.common.isBusy,
        submitRequested: state.common.submitRequested,
        compositionParams: {
            ...state.initialSettings.compositionParams,
            composition: state.common.composition,
        },
    };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): IProps => {
    return {
        ...ownProps,
        setFormState: (state: FormState) => dispatch(setFormStateIfChanged(state)),
        saveForm: (data: IFormData) => dispatch(onFormSave(data)),

        openSubmitConfirmation: () => dispatch(setSubmitRequested(true)),
        closeSubmitConfirmation: () => dispatch(setSubmitRequested(false)),
        removeClientFile: (fileId: number) => dispatch(removeClientFile(fileId)),
        showStripeModal: (updateFieldCallback: (value) => void) => dispatch(showStripeDialog(updateFieldCallback)),
        redirectToFormsPage: () => dispatch(goToFormsPage()),
    };
};

export const CustomFormConnected: ConnectedComponent<typeof CustomForm, any> = connect(
    mapStateToProps,
    mapDispatchToProps
)(CustomForm);
